// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

export interface NewsletterSchedule {
  id: string;
  newsletter_name: string;
  template_id: string;
  audience_segment_id: string;
  day_of_week: number;
  send_time: string;
  is_active: boolean;
  include_rss_feeds: boolean;
  include_latest_athletes: boolean;
  include_latest_events: boolean;
  include_latest_camps: boolean;
  custom_content_html?: string;
  gender?: string;
  sport?: string;
  created_at?: string;
  updated_at?: string;
  last_sent_at?: string;
  // Joined data
  segment_name?: string;
  template_name?: string;
}

export function useNewsletterSchedules() {
  const [schedules, setSchedules] = useState<NewsletterSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchSchedules = async () => {
    try {
      setIsLoading(true);
      
      console.log('🔄 Fetching newsletter schedules...');
      
      // Fetch schedules without joins first
      const { data: schedulesData, error: schedulesError } = await supabase
        .from('weekly_newsletter_schedules')
        .select('*')
        .order('created_at', { ascending: false });

      console.log('📦 Raw schedules data:', schedulesData);
      console.log('❌ Schedules error:', schedulesError);

      if (schedulesError) throw schedulesError;

      // Fetch segments
      const { data: segments } = await supabase
        .from('email_audience_segments')
        .select('id, segment_name');

      // Fetch templates
      const { data: templates } = await supabase
        .from('communication_templates')
        .select('id, name');

      // Create lookup maps
      const segmentMap = new Map(segments?.map(s => [s.id, s.segment_name]) || []);
      const templateMap = new Map(templates?.map(t => [t.id, t.name]) || []);

      // Combine data
      const formattedData = (schedulesData || []).map((schedule: any) => ({
        ...schedule,
        segment_name: segmentMap.get(schedule.audience_segment_id),
        template_name: templateMap.get(schedule.template_id),
      }));

      console.log('✅ Formatted schedules:', formattedData);
      setSchedules(formattedData);
    } catch (error: any) {
      console.error('Error fetching newsletter schedules:', error);
      toast.error('Failed to load newsletter schedules');
    } finally {
      setIsLoading(false);
    }
  };

  const createSchedule = async (scheduleData: Omit<NewsletterSchedule, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('weekly_newsletter_schedules')
        .insert([scheduleData])
        .select()
        .single();

      if (error) throw error;

      toast.success('Newsletter schedule created successfully');
      await fetchSchedules();
      return data;
    } catch (error: any) {
      console.error('Error creating newsletter schedule:', error);
      toast.error('Failed to create newsletter schedule');
      throw error;
    }
  };

  const updateSchedule = async (id: string, updates: Partial<NewsletterSchedule>) => {
    try {
      const { error } = await supabase
        .from('weekly_newsletter_schedules')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast.success('Newsletter schedule updated successfully');
      await fetchSchedules();
    } catch (error: any) {
      console.error('Error updating newsletter schedule:', error);
      toast.error('Failed to update newsletter schedule');
      throw error;
    }
  };

  const deleteSchedule = async (id: string) => {
    try {
      const { error } = await supabase
        .from('weekly_newsletter_schedules')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Newsletter schedule deleted successfully');
      await fetchSchedules();
    } catch (error: any) {
      console.error('Error deleting newsletter schedule:', error);
      toast.error('Failed to delete newsletter schedule');
      throw error;
    }
  };

  useEffect(() => {
    fetchSchedules();

    // Set up real-time subscription
    const channel = supabase
      .channel('newsletter-schedules-changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'weekly_newsletter_schedules' 
        }, 
        () => {
          console.log('📡 Newsletter schedule changed, refreshing...');
          fetchSchedules();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return {
    schedules,
    isLoading,
    createSchedule,
    updateSchedule,
    deleteSchedule,
    refetch: fetchSchedules,
  };
}
